<template>
    <div
        class="c-m-card c-m-card--with-actions molecule-card-company"
        :class="{
            'c-m-card--horizontal': horizontalLayout,
            'c-m-card--with-image': featuredImage
        }"
    >
        <router-link
            v-if="featuredImage"
            tag="div"
            class="c-m-card__hero c-m-card__hero--linked"
            :to="link"
        >
            <lazy-image
                v-if="featuredImage.sizes && horizontalLayout"
                :srcset="
                    `${featuredImage.sizes.card_4_3_medium} 960w, ${featuredImage.sizes.card_4_3_small} 480w`
                "
                :sizes="'(max-width: 480px) 480px, 960px'"
                :src="featuredImage.sizes.card_4_3_medium"
                :alt="altText"
            />
            <lazy-image
                v-else-if="!featuredImage.sizes && horizontalLayout"
                :src="featuredImage"
                :alt="altText"
            />
            <lazy-image
                v-else-if="featuredImage.sizes && !horizontalLayout"
                :srcset="
                    `${featuredImage.sizes.card_4_3_medium} 960w, ${featuredImage.sizes.card_4_3_small} 480w`
                "
                sizes="'(max-width: 480px) 480px, 960px'"
                :src="featuredImage.sizes.card_4_3_medium"
                :alt="altText"
            />
            <lazy-image
                v-else-if="!featuredImage.sizes && !horizontalLayout"
                :src="featuredImage"
                :alt="altText"
            />
        </router-link>
        <router-link
            v-else-if="!horizontalLayout"
            tag="div"
            class="c-m-card__hero c-m-card__hero--linked c-m-card__hero--no-image"
            :to="link"
        >
            <icon :icon="'hammer_and_wrench'"></icon>
        </router-link>
        <div class="c-m-card__content">
            <div class="c-m-card__headline">
                <h3 class="c-m-card__title">
                    <router-link :to="link">{{ title }}</router-link>
                </h3>
                <span class="c-m-card__ratings">
                    <atom-rating
                        :ratingValue="ratings"
                        :nrOfRatings="ratingsInfo"
                    ></atom-rating>
                </span>
            </div>
            <div v-if="certificates || place" class="c-m-card__byline">
                <span class="c-m-card__byline-item" v-if="place">
                    <icon :icon="'pin_drop'"></icon>{{ place }}
                </span>
                <span
                    class="c-m-card__byline-item c-m-card__byline-item--multi"
                    v-if="certificates.length > 0"
                >
                    <icon :icon="'certificate'"></icon>
                    <span
                        class="c-m-card__byline-sub-item"
                        v-for="(item, index) in certificates"
                        :key="`cert-${index}`"
                    >
                        {{ item.Name }}
                    </span>
                </span>
            </div>
            <p
                v-if="description"
                class="c-m-card__body"
                v-html="description"
            ></p>
        </div>
        <div class="c-m-card__actions">
            <request-button
                v-if="isQueued"
                v-tooltip.top-center="
                    'Du har redan valt hantverkaren till din förfrågan'
                "
                :icon="'list_add'"
                :disabled="true"
                class="c-m-card__button c-a-button--circle c-a-button--theme-primary"
                aria-label="Du har redan valt hantverkaren till din förfrågan"
            >
            </request-button>
            <request-button
                v-else-if="queueAtMaximum"
                v-tooltip.top-center="
                    'Du kan inte välja fler hantverkare till förfrågan'
                "
                :icon="'list_add'"
                :disabled="true"
                class="c-m-card__button c-a-button--circle c-a-button--theme-primary"
                aria-label="Du kan inte välja fler hantverkare till förfrågan"
            >
            </request-button>
            <request-button
                v-else
                v-tooltip.top-center="'Välj hantverkare till förfrågan'"
                :icon="'list_add'"
                class="c-m-card__button c-a-button--circle c-a-button--theme-primary"
                @buttonClicked="queueCompany(...[company])"
                aria-label="Välj hantverkare till förfrågan"
            >
            </request-button>
            <button-link
                class="c-a-button--circle c-m-card__button"
                :link="link"
                :icon="icon"
                aria-label="Gå vidare"
            ></button-link>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
    GET_REGIONS,
    GET_CERTIFICATES,
    GET_REGION_BY_ID
} from "../../../store/store-models/filters-model";
import {
    ADD_TO_QUEUE,
    GET_COMPANIES
} from "./../../../store/store-models/queued-companies-model";
import { EventBus } from "./../../../main";

import filters from "../../../filters/index.js";
import RequestButton from "../atoms/AtomButton.vue";
import ButtonLink from "../atoms/AtomButtonLink.vue";
import Icon from "../atoms/AtomIcon.vue";
import AtomRating from "../atoms/AtomRating.vue";

import LazyImage from "../atoms/LazyImage.vue";

export default {
    data() {
        return {
            icon: "arrow_forward",
            defaultImage: "Brabyggare-logo.png",
            showTooltip: false
        };
    },
    props: ["company", "horizontalLayout"],
    components: {
        RequestButton,
        ButtonLink,
        AtomRating,
        Icon,
        LazyImage
    },
    computed: {
        ...mapGetters({
            regions: GET_REGIONS,
            certificateMeta: GET_CERTIFICATES,
            queuedCompanies: GET_COMPANIES,
            regionById: GET_REGION_BY_ID
        }),
        featuredImage: function() {
            return this.company.Image ? this.company.Image : null;
        },
        altText: function() {
            return "";
        },
        link: function() {
            return "/hantverkare/" + this.company.Id + "/";
        },
        title: function() {
            return this.company.Title;
        },
        description: function() {
            return this.company.Presentation
                ? filters.truncate(
                      filters.stripHTML(this.company.Presentation),
                      300,
                      true
                  )
                : "";
        },
        ratings: function() {
            return this.company.Rating;
        },
        ratingsInfo: function() {
            return this.company.NrOfRatings;
        },
        certificates: function() {
            let certs = [];
            if (
                this.company.Certificates != null &&
                this.certificateMeta.length != null
            ) {
                this.company.Certificates.forEach(cert => {
                    const newCert = this.certificateMeta.find(
                        metaCert =>
                            metaCert.CertID === cert && metaCert.CertID !== 30
                    );

                    if (typeof newCert !== "undefined") {
                        certs.push(newCert);
                    }
                });
            }
            return certs;
        },
        place: function() {
            if (this.company.HomeRegId) {
                return this.regionById(this.company.HomeRegId);
            }
            if (this.company.VisitCity) {
                return this.company.VisitCity.replace(/([1-9])\w+/g, "");
            }
            return null;
        },
        isQueued: function() {
            let matches = this.queuedCompanies.filter(
                c => c.Id === this.company.Id
            );

            return matches.length > 0 ? true : false;
        },
        queueAtMaximum: function() {
            return this.queuedCompanies.length >= 6 ? true : false;
        }
    },
    methods: {
        ...mapActions({
            addToQueue: ADD_TO_QUEUE
        }),
        async queueCompany(company) {
            await this.addToQueue(company);

            EventBus.$emit("displayQueuedCompanies");
        }
    }
};
</script>



// WEBPACK FOOTER //
// src/views/components/molecules/MoleculeCardCompany.vue